import { useState } from "react";
import { apiSend } from "@/lib/api";
import type { Device } from "@/mocks/devices";

const TYPE_LABEL: Record<string, string> = { glass: "AI眼镜", phone: "手机" };

function ModalShell({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ---- 录入 / 编辑设备 ----

export function CreateDeviceModal({
  device,
  shipOptions,
  groupOptions,
  onClose,
  onCreated,
}: {
  /** present = edit mode */
  device?: Device;
  shipOptions: string[];
  groupOptions: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const isEdit = !!device;
  const [form, setForm] = useState({
    deviceType: device?.deviceType ?? "glass",
    deviceNo: device?.deviceNo ?? "",
    hardwareId: device?.hardwareId ?? "",
    mac: device?.mac ?? "",
    model: device?.model ?? "",
    firmware: device?.firmware ?? "",
    battery: device?.battery ?? 100,
    status: device?.status ?? "offline",
    ship: device?.ship ?? (shipOptions.find((s) => s !== "全部船次") ?? ""),
    group: device?.group ?? (groupOptions.find((g) => g !== "全部分组") ?? "未分组"),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k: string, v: string | number) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const body = {
        deviceType: form.deviceType,
        deviceNo: form.deviceNo.trim(),
        hardwareId: form.hardwareId.trim() || null,
        mac: form.mac.trim(),
        model: form.model.trim(),
        firmware: form.firmware.trim(),
        battery: Number(form.battery) || 0,
        status: form.status,
        ship: form.ship,
        group: form.group,
      };
      if (isEdit) {
        // PUT replaces all columns — carry bindings through unchanged.
        await apiSend("PUT", `/devices/${device.id}`, {
          ...body,
          boundVisitor: device.boundVisitor,
          trip: device.trip,
          pairingCode: device.pairingCode,
          lastOnline: device.lastOnline,
        });
      } else {
        await apiSend("POST", "/devices", body);
      }
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const input =
    "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500";

  return (
    <ModalShell
      title={isEdit ? `编辑设备 ${device.deviceNo}` : "录入设备"}
      subtitle={isEdit ? "设备自注册的设备由心跳维护在线状态与电量" : "编号留空将按 SX-{G|P}-{年月}-{序号} 自动分配"}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            设备类型
            {isEdit && device?.pairedDeviceNo && (
              <span className="ml-2 text-gray-400 font-normal">已配对设备不可改类型</span>
            )}
          </label>
          <div className="flex gap-2">
            {(["glass", "phone"] as const).map((t) => (
              <button
                key={t}
                onClick={() => set("deviceType", t)}
                disabled={isEdit && !!device?.pairedDeviceNo}
                className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                  form.deviceType === t
                    ? "bg-teal-50 border-teal-300 text-teal-700"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <i className={`${t === "glass" ? "ri-glasses-line" : "ri-smartphone-line"} mr-1.5`} />
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">设备编号（留空自动生成）</label>
            <input className={input} value={form.deviceNo} onChange={(e) => set("deviceNo", e.target.value)} placeholder="自动: SX-G-2606-001" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">硬件ID（唯一识别号）</label>
            <input className={input} value={form.hardwareId} onChange={(e) => set("hardwareId", e.target.value)} placeholder="如 a2649877c679d8fd" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">MAC</label>
            <input className={input} value={form.mac} onChange={(e) => set("mac", e.target.value)} placeholder="AC:23:3F:.." />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">型号</label>
            <input className={input} value={form.model} onChange={(e) => set("model", e.target.value)} placeholder={form.deviceType === "glass" ? "Rokid RG-glasses" : "Redmi Note 13"} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">固件/系统版本</label>
            <input className={input} value={form.firmware} onChange={(e) => set("firmware", e.target.value)} placeholder="v2.3.1" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">电量 %</label>
            <input className={input} type="number" min={0} max={100} value={form.battery} onChange={(e) => set("battery", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">状态</label>
            <select className={input} value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="offline">离线</option>
              <option value="online">在线</option>
              <option value="maintenance">维护中</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">所属船次</label>
            <select className={input} value={form.ship} onChange={(e) => set("ship", e.target.value)}>
              {shipOptions.filter((s) => s !== "全部船次").map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">分组</label>
            <select className={input} value={form.group} onChange={(e) => set("group", e.target.value)}>
              {[...new Set(["未分组", ...groupOptions.filter((g) => g !== "全部分组")])].map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {submitting ? "提交中..." : isEdit ? "保存修改" : "确认录入"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ---- 设备配对（眼镜 ↔ 手机 1-1）----

export function PairDeviceModal({
  device,
  candidates,
  onClose,
  onPaired,
}: {
  device: Device;
  candidates: Device[];
  onClose: () => void;
  onPaired: () => void;
}) {
  const [peerId, setPeerId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const targetType = (device.deviceType ?? "glass") === "glass" ? "phone" : "glass";
  const list = candidates.filter(
    (d) => (d.deviceType ?? "glass") === targetType && !d.pairedDeviceNo && d.id !== device.id,
  );

  const submit = async () => {
    if (!peerId) return;
    setSubmitting(true);
    setError("");
    try {
      await apiSend("POST", `/devices/${device.id}/pair-device`, { peerId });
      onPaired();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="设备配对"
      subtitle={`为 ${device.deviceNo}（${TYPE_LABEL[device.deviceType ?? "glass"]}）选择要配对的${TYPE_LABEL[targetType]}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        {list.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400">
            <i className="ri-link-unlink-m text-3xl block mb-2" />
            暂无可配对的{TYPE_LABEL[targetType]}（需未配对状态）
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto space-y-2">
            {list.map((d) => (
              <button
                key={d.id}
                onClick={() => setPeerId(d.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-colors ${
                  peerId === d.id
                    ? "bg-teal-50 border-teal-300"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div>
                  <div className="text-sm font-medium text-gray-900 font-mono">{d.deviceNo}</div>
                  <div className="text-xs text-gray-500">
                    {d.model || "-"}
                    {d.hardwareId && <span className="ml-2 font-mono text-gray-400">{d.hardwareId}</span>}
                  </div>
                </div>
                {peerId === d.id && <i className="ri-check-line text-teal-600" />}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={submit}
            disabled={!peerId || submitting}
            className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {submitting ? "配对中..." : "确认配对"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ---- 预配对结果（游客配对码）----

export interface PairingCodeResult {
  deviceNo: string;
  code: string;
}

export function PairingCodesModal({
  results,
  onClose,
}: {
  results: PairingCodeResult[];
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    const text = results.map((r) => `${r.deviceNo}: ${r.code}`).join("\n");
    await navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <ModalShell title="预配对完成" subtitle={`已为 ${results.length} 台设备生成配对码（24小时内有效）`} onClose={onClose}>
      <div className="space-y-4">
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {results.map((r) => (
            <div key={r.deviceNo} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-sm font-mono text-gray-700">{r.deviceNo}</span>
              <span className="text-base font-mono font-bold text-teal-700 tracking-widest">{r.code}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={copyAll}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <i className={`${copied ? "ri-check-line" : "ri-file-copy-line"} mr-1.5`} />
            {copied ? "已复制" : "复制全部"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
          >
            完成
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
